// FE-SCRM-038 — Producer landing page KPI strip.
//
// Surfaces a compact 4-tile summary above the Renewal Board toolbar
// when the active saved view is the pinned-default ("My book"). All
// four KPIs derive from the existing BE-SCRM-007 board response —
// no new BE endpoint required:
//
//   1. Pipeline value      — sum of ``total_value_estimate`` across
//                            every non-terminal cell.
//   2. Open opps           — sum of ``count`` across every non-
//                            terminal cell.
//   3. Critical            — sum of ``cadence_health_distribution
//                            .critical`` across every non-terminal
//                            cell.
//   4. Behind              — sum of ``cadence_health_distribution
//                            .behind`` across every non-terminal
//                            cell.
//
// The strip hides automatically when a different preset is active
// (saved view picked from the switcher) so producers comparing two
// books don't get the wrong base aggregates.

import type { RenewalBoardResponse } from "./types";


export interface MyBookKpiStripProps {
  /** True when the active saved view is the pinned-default
   * ("My book"). The strip renders ``null`` when this is false so
   * the producer sees the unadorned toolbar on every other view. */
  visible: boolean;
  /** Board response from the same hook the Grid + List + Kanban
   * read. ``null`` while in flight; renders four placeholder tiles
   * so the strip doesn't pop in/out as the response lands. */
  board: RenewalBoardResponse | null;
}


interface ComputedKpis {
  pipeline_value_cents: number | null;
  open_count: number;
  critical_count: number;
  behind_count: number;
}


function isTerminalStage(stage: string): boolean {
  return stage === "closed_won" || stage === "closed_lost";
}


export function computeMyBookKpis(
  board: RenewalBoardResponse | null,
): ComputedKpis | null {
  if (!board) return null;
  let pipeline_value_cents = 0;
  let any_value_known = false;
  let open_count = 0;
  let critical_count = 0;
  let behind_count = 0;
  for (const column of board.columns) {
    for (const cell of column.cells) {
      if (isTerminalStage(cell.stage)) continue;
      open_count += cell.count;
      if (cell.total_value_estimate !== null) {
        pipeline_value_cents += cell.total_value_estimate;
        any_value_known = true;
      }
      critical_count += cell.cadence_health_distribution.critical ?? 0;
      behind_count += cell.cadence_health_distribution.behind ?? 0;
    }
  }
  return {
    pipeline_value_cents: any_value_known ? pipeline_value_cents : null,
    open_count, critical_count, behind_count,
  };
}


function formatCentsCompact(cents: number | null): string {
  if (cents === null) return "—";
  const dollars = cents / 100;
  // Use a tight compact format for the chip — "$1.2M" beats
  // "$1,200,000" inside a 4-wide strip.
  return new Intl.NumberFormat("en-US", {
    style: "currency", currency: "USD",
    notation: "compact", maximumFractionDigits: 1,
  }).format(dollars);
}


export function MyBookKpiStrip({
  visible, board,
}: MyBookKpiStripProps): JSX.Element | null {
  if (!visible) return null;
  const kpis = computeMyBookKpis(board);
  return (
    <div
      data-testid="my-book-kpi-strip"
      className={[
        "grid grid-cols-2 gap-2 sm:grid-cols-4",
        "rounded-md border border-border-subtle bg-raised p-3",
      ].join(" ")}
      aria-label="My book — at-a-glance"
    >
      <KpiTile
        testid="my-book-kpi-pipeline-value"
        label="Pipeline value"
        value={
          kpis ? formatCentsCompact(kpis.pipeline_value_cents) : "—"
        }
        accent="teal"
      />
      <KpiTile
        testid="my-book-kpi-open-count"
        label="Open opps"
        value={kpis ? String(kpis.open_count) : "—"}
        accent="teal"
      />
      <KpiTile
        testid="my-book-kpi-critical-count"
        label="Critical"
        value={kpis ? String(kpis.critical_count) : "—"}
        accent={kpis && kpis.critical_count > 0 ? "ember" : "muted"}
      />
      <KpiTile
        testid="my-book-kpi-behind-count"
        label="Behind"
        value={kpis ? String(kpis.behind_count) : "—"}
        accent={kpis && kpis.behind_count > 0 ? "gold" : "muted"}
      />
    </div>
  );
}


function KpiTile({
  testid, label, value, accent,
}: {
  testid: string;
  label: string;
  value: string;
  accent: "teal" | "ember" | "gold" | "muted";
}) {
  const valueColor =
    accent === "ember" ? "text-ember-ink"
      : accent === "gold" ? "text-gold-ink"
        : accent === "muted" ? "text-text-tertiary"
          : "text-text-primary";
  return (
    <div
      data-testid={testid}
      className="flex flex-col gap-0.5"
    >
      <span
        className={[
          "text-[10px] uppercase tracking-wide text-text-tertiary",
        ].join(" ")}
      >
        {label}
      </span>
      <span className={`text-base font-semibold tracking-tight ${valueColor}`}>
        {value}
      </span>
    </div>
  );
}
